/**
 * Feature 14 — Public directory page at /directory.
 *
 * Card grid of security companies.  Mix of GuardGrade participants
 * (claimed, with real score data) and unclaimed directory entries
 * that can be claimed by a representative.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { directoryApi } from '../services/directory';
import LoadingSpinner from '../components/LoadingSpinner';
import type { DirectoryCompany } from '../types';


function StatusBadge({ status }: { status: DirectoryCompany['status'] }) {
  if (status === 'claimed') {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-700">
        Claimed
      </span>
    );
  }
  if (status === 'claim_pending') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-medium text-amber-700">
        Claim pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
      Unclaimed
    </span>
  );
}


function riskBandClasses(score: number): string {
  // Mirrors the public risk-label palette defined in CLAUDE.md
  // (Low Risk 750-900 green, Moderate 650-749 yellow, Elevated <650 red).
  if (score >= 750) return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  if (score >= 650) return 'bg-amber-50 border-amber-200 text-amber-700';
  return 'bg-red-50 border-red-200 text-red-700';
}


function ScoreChip({ score }: { score: number | null }) {
  if (score == null) {  // covers null and undefined
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2.5 py-0.5 text-xs font-medium text-slate-500">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
        Score pending
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${riskBandClasses(score)}`}>
      Score {score}
    </span>
  );
}


function DirectoryCard({ row }: { row: DirectoryCompany }) {
  return (
    <Link
      to={`/v/${row.slug}`}
      className="group block rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-brand-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900 group-hover:text-brand-600">
          {row.name}
        </h3>
        <StatusBadge status={row.status} />
      </div>
      <p className="mt-1 text-sm text-slate-500">
        {row.city}, {row.state}
      </p>
      {row.year_founded !== null && (
        <p className="mt-3 text-xs text-slate-400">
          Founded {row.year_founded}
        </p>
      )}
      <div className="mt-4 flex items-center justify-between">
        <ScoreChip score={row.score} />
        <span className="text-xs font-medium text-brand-600 opacity-0 transition group-hover:opacity-100">
          View profile →
        </span>
      </div>
    </Link>
  );
}


function CardSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse">
      <div className="flex items-start justify-between gap-3">
        <div className="h-4 w-40 rounded bg-slate-200" />
        <div className="h-4 w-16 rounded-full bg-slate-200" />
      </div>
      <div className="mt-2 h-3 w-28 rounded bg-slate-200" />
      <div className="mt-4 h-3 w-20 rounded bg-slate-200" />
      <div className="mt-4 h-5 w-24 rounded-full bg-slate-200" />
    </div>
  );
}


export default function PublicDirectory() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['directory', page, search, stateFilter, statusFilter],
    queryFn: () => directoryApi.list({
      page: String(page),
      search: search || undefined,
      state: stateFilter || undefined,
      status: statusFilter || undefined,
    }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;
  const hasResults = !!(data && data.results.length > 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-12">
        <h1 className="text-3xl font-semibold text-slate-900">
          Security Company Directory
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Browse security companies with objective performance scores.
          Unclaimed entries can be claimed by a real representative.
        </p>
        {data && (
          <p className="mt-1 text-xs text-slate-400">
            {data.count} {data.count === 1 ? 'company' : 'companies'} on GuardGrade
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 min-w-[200px] rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <input
            type="text"
            placeholder="State (e.g. CA)"
            value={stateFilter}
            onChange={(e) => { setStateFilter(e.target.value); setPage(1); }}
            className="w-32 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">All</option>
            <option value="claimed">Claimed</option>
            <option value="claim_pending">Claim pending</option>
            <option value="unclaimed">Unclaimed</option>
          </select>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
            </div>
          ) : !hasResults ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
              <p className="font-medium text-slate-700">No matches.</p>
              <p className="mt-1 text-xs text-slate-400">
                Try a different search term or clear the filters.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data!.results.map((row) => (
                <DirectoryCard key={row.id} row={row} />
              ))}
            </div>
          )}
        </div>

        {data && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-end gap-2 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
